import {
  defaultAgregationRequest,
  defaultAllCasesPageRequest,
  defaultAllCasesRequestFields
} from './constants/CasesConstants';
import axiosInstance, { ApiKeys, getApiUrl } from '../../utils/ApiHelper';
import {
  setCaseLevelFiltersData,
  setCaseLevelFiltersLoading,
  setCasesTableDataAndPagination,
  setLoading,
  setNextCases,
  setPausedCases,
  setPrevCases
} from './reducers/CasesSlice';
import {
  AllCasesApiResponseData,
  AllCasesPageRequest,
  CaseLevelFilters
} from './constants/CasesInterfaces';
import { pushToLocalStorage } from '../../utils/StorageUtils';
import { LOCAL_STORAGE_KEYS } from '../../constants/StorageKeys';
import { Dispatch } from '@reduxjs/toolkit';
import store from '@cp/src/store';

export enum SETTER_FUNCTION {
  CURRENT = 'CURRENT',
  NEXT = 'NEXT',
  PREV = 'PREV'
}

interface dispatchProps {
  payload: AllCasesApiResponseData;
  type: string;
}

export enum FETCH_ABORT_REASON {
  MULTIPLE_REQUEST = 'Abort multiple requests',
  MINIMUM_CHARACTERS_REQUIRED = 'Minimum characters required',
  TIMEOUT = 'Timeout'
}

const PAGE_SIZE = 20;
export const fetchAllCases = (
  searchPageRequest: AllCasesPageRequest = defaultAllCasesPageRequest,
  loanId?: string,
  customerReferenceId?: string,
  setterFunction?: SETTER_FUNCTION,
  updateParams = true
) => {
  const url = getApiUrl(ApiKeys.FETCH_ALL_CASES);
  return function (dispatch: ({ payload, type }: dispatchProps) => void) {
    setterFunction !== SETTER_FUNCTION.NEXT && dispatch(setLoading(true));
    const payload = {
      ...defaultAllCasesRequestFields,
      ...defaultAllCasesPageRequest,
      ...searchPageRequest,
      aggregationRequest: defaultAgregationRequest
    };

    if (payload && !payload.paymentStatus?.length) {
      payload.paymentStatus = ['ALL'];
    }
    if (updateParams) {
      pushToLocalStorage(LOCAL_STORAGE_KEYS.ALLOCATED_CASES_PAGE_REQUEST, payload);
    }

    const csaUiRevampEnabled = store.getState().common?.featureFlags?.csaUiRevampEnabled;

    // Map teleInteractionStatuses to interactionStatuses for API compatibility
    // Only for non-CSA agents
    // To be fixed later from BE and removed from here
    if (payload.teleInteractionStatuses && !csaUiRevampEnabled) {
      payload.interactionStatuses = payload.teleInteractionStatuses;
      delete payload.teleInteractionStatuses;
    }

    // Map allocatedFieldAgent to allocatedFieldAgentReferenceIds for API compatibility
    // Only for non-CSA agents
    // To be fixed later from BE and removed from here
    if (payload.allocatedFieldAgent) {
      payload.allocatedFieldAgentReferenceIds = payload.allocatedFieldAgent;
      delete payload.allocatedFieldAgent;
    }

    axiosInstance
      .post(url, payload, { headers: { includeInUptimeMetrics: true } })
      .then(response => {
        if (response.data?.caseSummaryPaginatedResponse) {
          switch (setterFunction) {
            case SETTER_FUNCTION.CURRENT:
              dispatch(
                setCasesTableDataAndPagination({
                  responseData: response.data,
                  loanId,
                  customerRefrenceId: customerReferenceId
                })
              );
              break;
            case SETTER_FUNCTION.NEXT:
              dispatch(
                setNextCases({
                  responseData: response.data,
                  loanId,
                  customerRefrenceId: customerReferenceId
                })
              );
              break;
            case SETTER_FUNCTION.PREV:
              dispatch(
                setPrevCases({
                  responseData: response.data,
                  loanId,
                  customerRefrenceId: customerReferenceId
                })
              );
              break;
            default:
              dispatch(
                setCasesTableDataAndPagination({
                  responseData: response.data,
                  loanId,
                  customerRefrenceId: customerReferenceId
                })
              );
              break;
          }
        }
      })
      .catch(() => {
        dispatch(setLoading(false));
      });
  };
};

export const fetchSearchedCases = (
  page: string | null,
  search: string,
  controller: AbortController
) => {
  setTimeout(() => {
    controller.abort();
  }, 10000); // 10 seconds

  const url = getApiUrl(ApiKeys.GLOBAL_SEARCH_CASES);

  return axiosInstance.post(
    url,
    {
      pages: {
        pageSize: PAGE_SIZE,
        nextPageToken: page || null
      },
      queryString: search
    },
    {
      signal: controller.signal,
      headers: { donotHandleError: true, includeInUptimeMetrics: true }
    }
  );
};

// Case level filters can be fetched and stored here
export const getCaseLevelFilters = (filterName: CaseLevelFilters) => (dispatch: Dispatch) => {
  dispatch(setCaseLevelFiltersLoading({ key: filterName, loading: true }));
  const url = getApiUrl(ApiKeys.CASE_FILTERS, null, { type: filterName });
  axiosInstance
    .get(url, { headers: { includeInUptimeMetrics: true } })
    .then(res => {
      if (res?.data) {
        dispatch(setCaseLevelFiltersData({ key: filterName, data: res.data }));
      }
    })
    .finally(() => {
      dispatch(setCaseLevelFiltersLoading({ key: filterName, loading: false }));
    });
};

export const fetchPausedCases = () => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.PAUSED_CASES);
  axiosInstance.get(url).then(res => {
    if (res?.data) {
      dispatch(setPausedCases(res.data));
    }
  });
};
